import { ChangeDetectionStrategy, Component, inject, input, linkedSignal, signal } from '@angular/core';
import { classes } from '@lite-workspace/shared/helm-ui/utils';
import { CachedImageDirective, ImagePreloadCacheService } from '@lite-workspace/shared/images';
import { cva, type VariantProps } from 'class-variance-authority';

/** Кольца-«плашки»: 2px шов цвета bg2 между фото и кольцом; выключенный альбом рвёт шов в пунктир. */
const PLATE_RINGS = ['rest', 'soft', 'elevated', 'choice', 'spotlight'] as const;

const avatarVariants = cva('relative grid place-items-center overflow-hidden rounded-full text-ink', {
  variants: {
    /** Сторона в шагах Tailwind; `full` — заполняет размерную обёртку хоста. */
    size: {
      '6': 'size-6',
      '7': 'size-7',
      '8': 'size-8',
      '9': 'size-9',
      '10': 'size-10',
      '11': 'size-11',
      '12': 'size-12',
      '16': 'size-16',
      '20': 'size-20',
      '23': 'size-23',
      '24': 'size-24',
      '27.5': 'size-27.5',
      '33': 'size-33',
      '38': 'size-38',
      '60': 'size-60',
      full: 'size-full',
    },
    ring: {
      /** Тонкое белое кольцо, только когда фото загрузилось. */
      auto: 'bg-panel2 ring-[var(--avatar-ring)]',
      none: 'bg-panel2',
      /** Кромка line2; загруженное фото получает и белое кольцо auto. */
      hairline: 'border border-line2 bg-panel2',
      outline: 'border-2 border-line bg-panel2 transition-colors duration-[var(--t-fast)] group-hover:border-c3',
      thick: 'bg-panel2 shadow-[var(--shadow-2)] ring-4 ring-[var(--avatar-ring)]',
      rest: 'border-2 bg-bg2',
      soft: 'border-2 bg-bg2',
      elevated: 'border-2 bg-bg2',
      choice: 'border-2 bg-bg2 transition-[box-shadow,transform] duration-[var(--t-fast)] ease-[var(--ease-smooth)]',
      spotlight: 'border-2 bg-bg2',
    },
    selected: { true: '', false: '' },
    off: { true: 'border-dashed border-line3 opacity-55', false: '' },
  },
  compoundVariants: [
    { ring: [...PLATE_RINGS], off: false, class: 'border-transparent' },
    { ring: ['hairline', 'outline'], selected: true, off: false, class: 'border-acc' },
    { ring: 'rest', off: false, class: 'shadow-[var(--avatar-ring-rest)]' },
    { ring: 'soft', off: false, class: 'shadow-[var(--shadow-avatar-ring-soft)]' },
    { ring: 'elevated', selected: false, class: 'shadow-[var(--avatar-ring-elevated)]' },
    {
      ring: 'elevated',
      off: true,
      selected: false,
      class:
        'transition-opacity duration-[var(--t-fast)] ease-[var(--ease-smooth)] motion-reduce:transition-none hover:opacity-100',
    },
    { ring: 'elevated', selected: true, class: 'shadow-[var(--shadow-avatar-picked-elevated)]' },
    {
      ring: 'choice',
      selected: false,
      class: 'shadow-[var(--avatar-ring-rest)] group-hover:scale-[1.04] group-hover:shadow-[var(--shadow-choice-hover)]',
    },
    { ring: 'choice', selected: true, class: 'scale-[1.04] shadow-[var(--shadow-choice-picked)]' },
    { ring: 'spotlight', class: 'shadow-[var(--shadow-avatar-spotlight)]' },
    {
      ring: ['auto', 'none', 'thick', 'rest', 'soft', 'spotlight'],
      selected: true,
      class: 'shadow-[var(--shadow-choice-picked)]',
    },
    // ring-* paints above box-shadow: the white thick ring would hide the accent.
    { ring: 'thick', selected: true, class: 'ring-0' },
  ],
  defaultVariants: { size: '10', ring: 'auto', selected: false, off: false },
});

type AvatarVariants = VariantProps<typeof avatarVariants>;
export type AvatarSize = NonNullable<AvatarVariants['size']>;
export type AvatarRing = NonNullable<AvatarVariants['ring']>;

@Component({
  selector: 'app-avatar',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CachedImageDirective],
  template: `
    <!-- Один силуэт на всю систему, никогда инициалы/прочерк. Размер в процентах — вид одинаков на аватаре
         любого размера; stroke 0.8 при боксе 78% даёт ту же толщину линии, что 1.3 при 48% (0.8×78 = 1.3×48). -->
    <svg
      class="text-c3"
      width="78%"
      height="78%"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="0.8"
      aria-hidden="true"
    >
      <circle cx="12" cy="9" r="4" />
      <path d="M5 20a7 7 0 0 1 14 0" />
    </svg>
    @if (src(); as url) {
      @if (!loaded() && !errored()) {
        <span class="absolute inset-0 animate-pulse bg-muted" aria-hidden="true"></span>
      }
      <img
        [appCachedImg]="url"
        (loadedChange)="onLoaded($event)"
        (loadError)="errored.set(true)"
        alt=""
        class="absolute inset-0 size-full object-cover transition-opacity duration-300"
        [class.opacity-0]="!loaded()"
      />
    }
  `,
})
export class AvatarComponent {
  private readonly cache = inject(ImagePreloadCacheService);

  readonly src = input<string | null>(null);
  readonly size = input<AvatarSize>('10');
  readonly ring = input<AvatarRing>('auto');
  /** Выбран: акцентное кольцо на любом кольце (контурные — акцентная кромка, elevated и choice — своё кольцо). */
  readonly selected = input(false);
  /** Альбом выключен: пунктирный шов, лицо приглушено; кольцо держит только невыбранный elevated, наведение его поднимает. */
  readonly off = input(false);

  // An already-cached src mounts VISIBLE: a route swap builds a fresh instance, and a bare false would
  // paint one opacity-0 frame and fade the very same cached face back in over 300ms. linkedSignal, not
  // a once-seeded signal — the next src re-derives instead of inheriting the first one's verdict.
  protected readonly loaded = linkedSignal({
    source: this.src,
    computation: url => !!url && this.cache.isLoaded(url),
  });

  protected readonly errored = signal(false);

  constructor() {
    classes(() => {
      const ring = this.ring();
      const look = avatarVariants({ size: this.size(), ring, selected: this.selected(), off: this.off() });
      const faceRing = (ring === 'auto' || ring === 'hairline') && this.loaded();
      return faceRing ? `${look} ring-1 ring-[var(--avatar-ring)]` : look;
    });
  }

  protected onLoaded(value: boolean): void {
    this.loaded.set(value);
    if (!value) this.errored.set(false);
  }
}
